import { useEffect, useRef, useState } from "react"
import { Alert, Button, Empty, Spin } from "antd"
import { useParams } from "react-router-dom"
import { useRequest } from "ahooks"
import { getProjectHQNewStagesApi, getProjectHQNewTasksApi, updateProjectHQNewTaskStatusApi } from "@/services/apis/projectDesignHQNew"
import { PhaseStepper } from "./components/PhaseStepper"
import { Sidebar } from "./components/Sidebar"
import { TaskItem } from "./components/TaskItem"
import { AttachmentManager } from "./components/AttachmentManager"

const DesignHQNew = () => {
  const { projectID = "" } = useParams()
  const [stageId, setStageId] = useState<number>()
  const [itemId, setItemId] = useState<number>()
  const [attachmentTask, setAttachmentTask] = useState<API.PROJECTHQ_NEW.Task | null>(null)
  const stages = useRequest(getProjectHQNewStagesApi, { manual: true })
  const tasks = useRequest(getProjectHQNewTasksApi, { manual: true })
  const selection = useRef({ projectID, itemId })
  selection.current = { projectID, itemId }
  const currentStage = stages.data?.find(stage => stage.id === stageId)
  const currentItem = currentStage?.projectStageTypes.find(item => item.id === itemId)

  useEffect(() => {
    stages.cancel(); tasks.cancel(); stages.mutate(undefined); tasks.mutate(undefined)
    setStageId(undefined); setItemId(undefined); setAttachmentTask(null)
    if (projectID) stages.run(projectID)
    return () => { stages.cancel(); tasks.cancel() }
  }, [projectID]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!stages.data) return
    const stage = stages.data.find(row => row.id === stageId) || stages.data[0]
    setStageId(stage?.id)
    setItemId(previous => stage?.projectStageTypes.find(item => item.id === previous)?.id ?? stage?.projectStageTypes[0]?.id)
  }, [stages.data, stageId])

  useEffect(() => {
    tasks.cancel(); tasks.mutate(undefined)
    if (itemId !== undefined) tasks.run(itemId, projectID)
  }, [itemId, projectID]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (task: API.PROJECTHQ_NEW.Task, status: 0 | 1) => {
    const context = projectID
    await updateProjectHQNewTaskStatusApi(task.id, status)
    if (selection.current.projectID !== context) return
    stages.run(context)
    if (selection.current.itemId === task.itemId) tasks.run(task.itemId, context)
  }
  if (stages.loading && !stages.data) return <div className="p-12 text-center"><Spin /></div>
  if (stages.error && !stages.data) return <Alert type="error" message="阶段加载失败" action={<Button onClick={() => stages.run(projectID)}>重试</Button>} />
  if (!stages.data?.length) return <Empty description="暂无数据" />
  return <div className="h-[calc(100vh-140px)] min-w-min flex flex-col">
    <PhaseStepper phases={stages.data} currentId={stageId} onChange={stage => { setStageId(stage.id); setItemId(stage.projectStageTypes[0]?.id); setAttachmentTask(null) }} />
    {stages.error && <Alert type="error" message="统计加载失败" action={<Button onClick={() => stages.run(projectID)}>重试</Button>} />}
    <div className="flex flex-1 min-h-0">
      <Sidebar items={currentStage?.projectStageTypes || []} currentId={itemId} onChange={item => { setItemId(item.id); setAttachmentTask(null) }} />
      <main className="flex-1 overflow-y-auto bg-blue-50 p-6">
        <div className="text-xs text-gray-500 mb-6">当前共有 <b>{currentItem?.totalCount || 0}</b> 项职责，其中未完成的有 <b>{currentItem?.uncompletedCount || 0}</b> 项</div>
        <div className="flex gap-3 px-4 py-2 text-xs text-gray-400"><div className="flex-1">任务名称</div><div className="w-[100px] text-center">状态</div><div className="w-[120px] text-center">操作</div></div>
        {tasks.error ? <Alert type="error" message="职责任务加载失败" action={<Button onClick={() => { if (itemId !== undefined) tasks.run(itemId, projectID) }}>重试</Button>} />
          : tasks.loading ? <div className="p-12 text-center"><Spin /></div>
          : tasks.data?.length ? tasks.data.map(task => <TaskItem key={task.id} task={task} onStatusChange={updateStatus} onAttachments={setAttachmentTask} />)
          : <div className="bg-white rounded-lg p-12 text-center"><Empty description="当前分类下暂无职责任务" /><p className="text-gray-400 text-sm mt-2">请切换分类查看或新增自定义任务</p></div>}
      </main>
    </div>
    {attachmentTask && <AttachmentManager key={attachmentTask.id} task={attachmentTask} onClose={() => setAttachmentTask(null)} />}
  </div>
}
export default DesignHQNew
